import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge } from 'react-bootstrap';
import { sprayService, SprayProduct } from '../../services/sprayService';
import { formatPhi } from '../../utils/productDisplay';
import { useMenu } from '../../auth/useMenu';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, DataTableColumn, StatusPill } from '../patterns';

interface Props {
  onAdd?: () => void;
  onEditProduct?: (product: SprayProduct) => void;
  onDeleteProduct?: (id: string) => void;
}

const SprayProductList: React.FC<Props> = ({ onAdd, onEditProduct, onDeleteProduct }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<SprayProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Any user with spraying:products access can manage products.
  const canManage = useMenu('spraying:products');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.products.title') },
  ]);

  useEffect(() => {
    sprayService.getProducts()
      .then(setProducts)
      .catch((err: { message?: string }) => setError(err.message ?? t('spray.products.loadError')))
      .finally(() => setLoading(false));
    // Mount-only fetch: t is treated as stable (we only use it for the error message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (product: SprayProduct) => {
    if (!window.confirm(t('spray.products.confirmDelete'))) return;
    try {
      await sprayService.deleteProduct(product._id);
      setProducts(prev => prev.filter(p => p._id !== product._id));
      setError(null);
      onDeleteProduct?.(product._id);
    } catch {
      setError(t('spray.products.saveError'));
    }
  };

  const openPdf = async (productId: string, field: 'label' | 'msds') => {
    try {
      const url = field === 'label'
        ? await sprayService.getProductLabelPdfSignedUrl(productId)
        : await sprayService.getProductMsdsPdfSignedUrl(productId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(t('spray.products.pdfErrors.uploadFailed'));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.productName.toLowerCase().includes(q) ||
      (p.lNumber ?? '').toLowerCase().includes(q)
    );
  }, [products, search]);

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <Alert variant="danger" role="alert">{error}</Alert>;

  const columns: DataTableColumn<SprayProduct>[] = [
    { key: 'productName', label: t('spray.products.name'), render: (p) => p.productName },
    { key: 'lNumber',     label: t('spray.products.lNumber'), render: (p) => p.lNumber },
    {
      key: 'needsReview',
      label: t('spray.products.needsReview.badge'),
      render: (p) =>
        p.needsReview ? (
          <StatusPill variant="warn">{t('spray.products.needsReview.badge')}</StatusPill>
        ) : null,
    },
    { key: 'category',    label: t('spray.products.category'), render: (p) => p.category ?? '—' },
    {
      key: 'dosePer100L',
      label: t('spray.products.dosePer100L'),
      render: (p) =>
        p.dosePer100L != null && p.dosePer100LUnit
          ? `${p.dosePer100L} ${p.dosePer100LUnit}`
          : '—',
    },
    {
      key: 'phi',
      label: t('spray.products.phi'),
      render: (p) => formatPhi(p.phi, t),
    },
    {
      key: 'documents',
      label: t('spray.products.documents'),
      render: (p) => {
        if (!p.labelPdf && !p.msdsPdf) return <span className="text-muted">—</span>;
        return (
          <span className="d-inline-flex gap-2">
            {p.labelPdf && (
              <button
                type="button"
                className="pat-btn pat-btn--subtle"
                aria-label={t('spray.products.iconLabelPdfTitle')}
                title={t('spray.products.iconLabelPdfTitle')}
                onClick={() => openPdf(p._id, 'label')}
              >
                <i className="bi bi-file-earmark-pdf-fill" aria-hidden="true" />
              </button>
            )}
            {p.msdsPdf && (
              <button
                type="button"
                className="pat-btn pat-btn--subtle"
                aria-label={t('spray.products.iconMsdsPdfTitle')}
                title={t('spray.products.iconMsdsPdfTitle')}
                onClick={() => openPdf(p._id, 'msds')}
              >
                <i className="bi bi-file-earmark-medical" aria-hidden="true" />
              </button>
            )}
          </span>
        );
      },
    },
    {
      key: 'source',
      label: t('spray.products.source'),
      render: (p) =>
        p.tenantId === null
          ? <Badge bg="secondary">{t('spray.products.global')}</Badge>
          : <Badge bg="primary">{t('spray.products.tenant')}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (p) =>
        canManage && p.tenantId !== null ? (
          <span className="d-inline-flex gap-2">
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => onEditProduct?.(p)}
            >
              {t('common.edit')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={() => handleDelete(p)}
            >
              {t('common.delete')}
            </button>
          </span>
        ) : null,
    },
  ];

  return (
    <div data-testid="spray-product-list">
      <PageHeader
        title={t('spray.products.title')}
        meta={t('spray.products.metaCount', { count: products.length, defaultValue: '{{count}} products' })}
        actions={canManage ? (
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => onAdd?.()}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.pageHeader.actions.newProduct')}
          </button>
        ) : null}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.products.searchPlaceholder')}
      />

      <DataTable<SprayProduct>
        rows={filtered}
        rowKey={(p) => p._id}
        columns={columns}
        emptyState={<div className="p-4 text-muted">{t('spray.products.noProducts')}</div>}
      />
    </div>
  );
};

export default SprayProductList;
